/**
 * FSRS v4.5 – Free Spaced Repetition Scheduler
 * Basiert auf: github.com/open-spaced-repetition/fsrs4anki
 *
 * Ratings: 1=Wieder  2=Schwer  3=Gut  4=Einfach
 * States:  new → learning → review (↔ relearning on Again)
 */

// Default-Gewichte (trainiert auf 400 Mio. Anki-Reviews)
const W = [
  0.4072, 1.1829, 3.1262, 15.4722,   // S_0 je Rating (1-4)
  7.2102, 0.5316, 1.0651, 0.0589,    // Difficulty
  1.506,  0.1544, 1.004,  1.9395,    // S nach Recall
  0.11,   0.29,   2.2700, 0.2500,    // S nach Forget
  2.9898, 0.51,   0.43,              // Easy-Bonus / Hard-Penalty / target
];

// Zielbehaltensrate 90 %
const DECAY  = -0.5;
const FACTOR = Math.pow(0.9, 1 / DECAY) - 1; // ≈ 0.2346 → I(S) ≈ S

const LEARN_STEPS_MIN    = [1, 10];  // Lernphasen-Schritte in Minuten
const RELEARN_STEPS_MIN  = [10];

const MIN_MS = 60_000;
const DAY_MS = 86_400_000;

export type Rating    = 1 | 2 | 3 | 4;
export type CardState = 'new' | 'learning' | 'review' | 'relearning';

export interface FSRSCard {
  stability:  number;  // S in Tagen
  difficulty: number;  // D in [1, 10]
  due:        number;  // Fälligkeits-Timestamp ms
  lastReview: number;  // letzter Review-Timestamp ms
  reps:       number;
  lapses:     number;
  state:      CardState;
  step:       number;  // aktueller Lernphasen-Index
}

export function newCard(): FSRSCard {
  return { stability: 0, difficulty: 5, due: 0, lastReview: 0,
           reps: 0, lapses: 0, state: 'new', step: 0 };
}

/* ---------- FSRS-Kern-Formeln ---------- */

function clamp(v: number, lo: number, hi: number) { return Math.min(Math.max(v, lo), hi); }

function initS(r: Rating)           { return Math.max(W[r - 1], 0.1); }
function initD(r: Rating)           { return clamp(W[4] - W[5] * (r - 3), 1, 10); }
function nextD(d: number, r: Rating){ return clamp(d - W[6] * (r - 3) * ((10 - d) / 9), 1, 10); }

// Retrievability: P(Erinnern) nach t Tagen mit Stabilität S
function R(t: number, S: number) { return Math.pow(1 + FACTOR * t / S, DECAY); }

// Neue Stabilität nach erfolgreichem Abruf
function nextSRecall(d: number, s: number, r: number, rating: Rating): number {
  const hp = rating === 2 ? W[15] : 1;   // Hard Penalty
  const eb = rating === 4 ? W[16] : 1;   // Easy Bonus
  return Math.max(0.1,
    s * (Math.exp(W[8]) * (11 - d) * Math.pow(s, -W[9]) *
      (Math.exp((1 - r) * W[10]) - 1) * hp * eb + 1));
}

// Neue Stabilität nach Vergessen (Again auf Review-Karte)
function nextSForget(d: number, s: number, r: number): number {
  return Math.max(0.1,
    W[11] * Math.pow(d, -W[12]) * (Math.pow(s + 1, W[13]) - 1) * Math.exp((1 - r) * W[14]));
}

// Intervall in Tagen für Zielbehaltensrate 90 %
function intervalDays(s: number) { return Math.max(1, Math.round(s)); }

/* ---------- Scheduling ---------- */

export function schedule(card: FSRSCard, rating: Rating): FSRSCard {
  const now = Date.now();
  const out: FSRSCard = { ...card, reps: card.reps + 1, lastReview: now };

  /* Neue Karte */
  if (card.state === 'new') {
    out.stability  = initS(rating);
    out.difficulty = initD(rating);
    if (rating === 1 || rating === 2) {
      out.state = 'learning'; out.step = 0;
      out.due   = now + LEARN_STEPS_MIN[0] * MIN_MS;
    } else if (rating === 3) {
      if (LEARN_STEPS_MIN.length > 1) {
        out.state = 'learning'; out.step = 1;
        out.due   = now + LEARN_STEPS_MIN[1] * MIN_MS;
      } else {
        out.state = 'review';
        out.due   = now + intervalDays(out.stability) * DAY_MS;
      }
    } else { // Easy: initS(4) ist bereits die "Einfach"-Stabilität – kein Extra-Bonus
      out.state = 'review';
      out.due   = now + intervalDays(out.stability) * DAY_MS;
    }
    return out;
  }

  /* Lern- / Wiederholungsphase (Minuten-Schritte) */
  if (card.state === 'learning' || card.state === 'relearning') {
    const steps = card.state === 'learning' ? LEARN_STEPS_MIN : RELEARN_STEPS_MIN;
    if (rating === 1) {
      out.step = 0; out.due = now + steps[0] * MIN_MS;
    } else if (rating === 2) {
      out.due = now + steps[card.step] * MIN_MS; // aktuellen Schritt wiederholen
    } else if (rating === 3) {
      const next = card.step + 1;
      if (next >= steps.length) {
        out.state = 'review';
        out.due   = now + intervalDays(out.stability) * DAY_MS;
      } else {
        out.step = next;
        out.due  = now + steps[next] * MIN_MS;
      }
    } else { // Easy: sofort graduieren, Stabilität mind. auf Easy-Niveau heben
      out.state = 'review';
      out.stability = Math.max(out.stability, initS(4));
      out.due   = now + intervalDays(out.stability) * DAY_MS;
    }
    return out;
  }

  /* Review-Karte */
  const elapsed = Math.max(0, (now - card.lastReview) / DAY_MS);
  const r       = R(elapsed, card.stability);
  out.difficulty = nextD(card.difficulty, rating);

  if (rating === 1) {
    out.lapses    = card.lapses + 1;
    out.stability = nextSForget(card.difficulty, card.stability, r);
    out.state     = 'relearning'; out.step = 0;
    out.due       = now + RELEARN_STEPS_MIN[0] * MIN_MS;
  } else {
    out.stability = nextSRecall(card.difficulty, card.stability, r, rating);
    out.state     = 'review';
    out.due       = now + intervalDays(out.stability) * DAY_MS;
  }
  return out;
}

/* ---------- Intervall-Vorschau (für Anki-Buttons) ---------- */

export function previewIntervals(card: FSRSCard): [string, string, string, string] {
  return ([1, 2, 3, 4] as Rating[]).map(r =>
    fmtInterval(schedule(card, r).due - Date.now())
  ) as [string, string, string, string];
}

export function fmtInterval(ms: number): string {
  const totalMin = Math.max(1, Math.round(ms / MIN_MS));
  if (totalMin < 60)   return `${totalMin} Min`;
  const totalH = Math.round(ms / (60 * MIN_MS));
  if (totalH  < 24)   return `${totalH} Std`;
  const totalD = Math.round(ms / DAY_MS);
  if (totalD  < 30)   return `${totalD} T`;
  const totalM = Math.round(totalD / 30);
  if (totalM  < 12)   return `${totalM} Mo`;
  return `${Math.round(totalD / 365)} J`;
}

export function fmtDue(due: number): string {
  const diff = due - Date.now();
  if (diff <= 0) return 'jetzt';
  return 'in ' + fmtInterval(diff);
}

/* ---------- localStorage-Persistenz ---------- */

const LS_PREFIX = 'fsrs_v1_';

function key(topicId: string, hash: string) {
  return `${LS_PREFIX}${topicId}:${hash}`;
}

export function loadCard(topicId: string, hash: string): FSRSCard {
  try {
    const raw = localStorage.getItem(key(topicId, hash));
    if (raw) return { ...newCard(), ...JSON.parse(raw) } as FSRSCard;
  } catch { /* ignore */ }
  return newCard();
}

export function saveCard(topicId: string, hash: string, card: FSRSCard): void {
  try { localStorage.setItem(key(topicId, hash), JSON.stringify(card)); }
  catch { /* ignore */ }
}

/** Stabile Karten-ID aus Vorder- + Rückseite. */
export function hashCard(front: string, back: string): string {
  let h = 0;
  const s = front + '\x00' + back;
  for (let i = 0; i < s.length; i++) {
    h = (Math.imul(31, h) + s.charCodeAt(i)) | 0;
  }
  return (h >>> 0).toString(36);
}

/** Alle gespeicherten Karten-States für ein Thema. */
export function topicStats(topicId: string): { total: number; due: number; new_: number } {
  const prefix = `${LS_PREFIX}${topicId}:`;
  const now    = Date.now();
  let total = 0, due = 0, new_ = 0;
  for (let i = 0; i < localStorage.length; i++) {
    const k = localStorage.key(i);
    if (!k?.startsWith(prefix)) continue;
    total++;
    try {
      const c = JSON.parse(localStorage.getItem(k) ?? '{}') as FSRSCard;
      if (c.state === 'new') new_++;
      else if (c.due <= now) due++;
    } catch { /* ignore */ }
  }
  return { total, due, new_ };
}
